/*
  The small form controls of this module. Each one ties its label, its hint and its error to the box, so a screen
  reader reads them together, and each one says in plain words what was wrong.
*/

import clsx from "clsx";
import { useId, type ReactNode, type TextareaHTMLAttributes } from "react";

import { Button, IconButton } from "../../../ui";

interface FrameProps {
  label: string;
  hint?: ReactNode;
  error?: string | null;
  required?: boolean;
  className?: string;
}

function describedBy(id: string, hint: unknown, error: unknown): string | undefined {
  return [hint ? `${id}-hint` : null, error ? `${id}-error` : null].filter(Boolean).join(" ") || undefined;
}

function Frame({ id, label, hint, error, required, className, children }: FrameProps & { id: string; children: ReactNode }) {
  return (
    <div className={clsx("field", className)}>
      <label htmlFor={id} className="field__label">
        {label}
        {required && <span className="field__required"> (needed)</span>}
      </label>
      {hint && (
        <p id={`${id}-hint`} className="field__hint muted">
          {hint}
        </p>
      )}
      {children}
      {error && (
        <p id={`${id}-error`} role="alert" className="field__error">
          {error}
        </p>
      )}
    </div>
  );
}

interface TextProps extends FrameProps {
  value: string;
  onChange: (value: string) => void;
  type?: "text" | "number" | "search";
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  maxLength?: number;
  /** The id of a `<datalist>` with suggestions. */
  list?: string;
}

/** One line of text. */
export function TextInput({ label, hint, error, required, className, value, onChange, type = "text", ...rest }: TextProps) {
  const id = useId();
  return (
    <Frame id={id} label={label} hint={hint} error={error} required={required} className={className}>
      <input
        id={id}
        className="field__input"
        type={type}
        value={value}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, hint, error)}
        onChange={(event) => onChange(event.target.value)}
        {...rest}
      />
    </Frame>
  );
}

interface AreaProps extends FrameProps {
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  placeholder?: string;
  disabled?: boolean;
  maxLength?: TextareaHTMLAttributes<HTMLTextAreaElement>["maxLength"];
}

/** Several lines of text. */
export function TextArea({ label, hint, error, required, className, value, onChange, rows = 3, ...rest }: AreaProps) {
  const id = useId();
  return (
    <Frame id={id} label={label} hint={hint} error={error} required={required} className={className}>
      <textarea
        id={id}
        className="field__input field__area"
        rows={rows}
        value={value}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, hint, error)}
        onChange={(event) => onChange(event.target.value)}
        {...rest}
      />
    </Frame>
  );
}

export interface Option {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SelectProps extends FrameProps {
  value: string;
  onChange: (value: string) => void;
  options: readonly Option[];
  /** The first line, for "nothing chosen yet". Left out when a choice is always made. */
  placeholder?: string;
  disabled?: boolean;
}

/** A list to choose from. */
export function Select({ label, hint, error, required, className, value, onChange, options, placeholder, disabled }: SelectProps) {
  const id = useId();
  return (
    <Frame id={id} label={label} hint={hint} error={error} required={required} className={className}>
      <select
        id={id}
        className="field__input"
        value={value}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, hint, error)}
        onChange={(event) => onChange(event.target.value)}
      >
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
    </Frame>
  );
}

interface CheckProps {
  label: string;
  hint?: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
}

/** A yes-or-no box with its words beside it. */
export function Check({ label, hint, checked, onChange, disabled, className }: CheckProps) {
  const id = useId();
  return (
    <div className={clsx("field field--check", className)}>
      <div className="field__checkline">
        <input id={id} type="checkbox" checked={checked} disabled={disabled} aria-describedby={hint ? `${id}-hint` : undefined} onChange={(event) => onChange(event.target.checked)} />
        <label htmlFor={id}>{label}</label>
      </div>
      {hint && (
        <p id={`${id}-hint`} className="field__hint muted">
          {hint}
        </p>
      )}
    </div>
  );
}

interface RadioProps {
  legend: string;
  hint?: ReactNode;
  error?: string | null;
  required?: boolean;
  value: string;
  onChange: (value: string) => void;
  options: readonly Option[];
  className?: string;
}

/** A few choices side by side where only one may be picked. */
export function RadioGroup({ legend, hint, error, required, value, onChange, options, className }: RadioProps) {
  const name = useId();
  return (
    <fieldset className={clsx("field field--group", className)} aria-invalid={error ? true : undefined}>
      <legend className="field__label">
        {legend}
        {required && <span className="field__required"> (needed)</span>}
      </legend>
      {hint && <p className="field__hint muted">{hint}</p>}
      <div className="field__choices">
        {options.map((option) => {
          const id = `${name}-${option.value}`;
          return (
            <span key={option.value} className="field__choice">
              <input id={id} type="radio" name={name} value={option.value} checked={value === option.value} disabled={option.disabled} onChange={() => onChange(option.value)} />
              <label htmlFor={id}>{option.label}</label>
            </span>
          );
        })}
      </div>
      {error && (
        <p role="alert" className="field__error">
          {error}
        </p>
      )}
    </fieldset>
  );
}

interface ListProps {
  label: string;
  hint?: ReactNode;
  values: readonly string[];
  onChange: (values: string[]) => void;
  addLabel: string;
  placeholder?: string;
  className?: string;
}

/** A list of short texts the person can add to and take from. */
export function StringList({ label, hint, values, onChange, addLabel, placeholder, className }: ListProps) {
  const base = useId();
  return (
    <fieldset className={clsx("field field--group", className)}>
      <legend className="field__label">{label}</legend>
      {hint && <p className="field__hint muted">{hint}</p>}
      <ul className="row-list">
        {values.map((value, index) => (
          <li key={index} className="row">
            <input
              id={`${base}-${index}`}
              className="field__input"
              aria-label={`${label}, entry ${index + 1}`}
              value={value}
              placeholder={placeholder}
              onChange={(event) => onChange(values.map((v, i) => (i === index ? event.target.value : v)))}
            />
            <IconButton label={`Take out entry ${index + 1} of ${label}`} onClick={() => onChange(values.filter((_v, i) => i !== index))}>
              ×
            </IconButton>
          </li>
        ))}
      </ul>
      <div>
        <Button variant="quiet" onClick={() => onChange([...values, ""])}>
          {addLabel}
        </Button>
      </div>
    </fieldset>
  );
}

export interface Pair {
  key: string;
  value: string;
}

interface PairProps {
  label: string;
  hint?: ReactNode;
  rows: readonly Pair[];
  onChange: (rows: Pair[]) => void;
  keyLabel: string;
  valueLabel: string;
  addLabel: string;
  className?: string;
}

/** Rows of two texts, such as a printed word and our name for it. */
export function PairList({ label, hint, rows, onChange, keyLabel, valueLabel, addLabel, className }: PairProps) {
  return (
    <fieldset className={clsx("field field--group", className)}>
      <legend className="field__label">{label}</legend>
      {hint && <p className="field__hint muted">{hint}</p>}
      <ul className="row-list">
        {rows.map((row, index) => (
          <li key={index} className="row">
            <input
              className="field__input"
              aria-label={`${keyLabel}, row ${index + 1}`}
              placeholder={keyLabel}
              value={row.key}
              onChange={(event) => onChange(rows.map((r, i) => (i === index ? { ...r, key: event.target.value } : r)))}
            />
            <input
              className="field__input"
              aria-label={`${valueLabel}, row ${index + 1}`}
              placeholder={valueLabel}
              value={row.value}
              onChange={(event) => onChange(rows.map((r, i) => (i === index ? { ...r, value: event.target.value } : r)))}
            />
            <IconButton label={`Take out row ${index + 1} of ${label}`} onClick={() => onChange(rows.filter((_r, i) => i !== index))}>
              ×
            </IconButton>
          </li>
        ))}
      </ul>
      <div>
        <Button variant="quiet" onClick={() => onChange([...rows, { key: "", value: "" }])}>
          {addLabel}
        </Button>
      </div>
    </fieldset>
  );
}

/** Turn rows of two texts into an object, leaving out rows with an empty key. */
export function pairsToRecord(rows: readonly Pair[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const row of rows) {
    const key = row.key.trim();
    if (key) out[key] = row.value.trim();
  }
  return out;
}

export function recordToPairs(record: unknown): Pair[] {
  if (!record || typeof record !== "object") return [];
  return Object.entries(record as Record<string, unknown>).map(([key, value]) => ({ key, value: typeof value === "string" ? value : String(value ?? "") }));
}

/** A line under a form that says why a step was refused. */
export function FormError({ message }: { message: string | null | undefined }) {
  if (!message) return null;
  return (
    <p role="alert" className="form-error">
      {message}
    </p>
  );
}
